import { useCallback, useEffect, useState } from 'react'

import { api } from '../api'
import LevelChart from '../components/LevelChart'
import { setLang, t, type Lang } from '../i18n'
import { isTelegramApp, setToken } from '../platform'
import { confirmAction, copyText, notify, openExternal, shareToChat, tg } from '../telegram'
import { telegramLogin } from '../telegramLogin'
import AdminPlayers from './AdminPlayers'
import AdminDeals from './AdminDeals'
import AdminCoaches from './AdminCoaches'
import CoachEditor from './CoachEditor'
import CoachInbox from './CoachInbox'
import Legal from '../components/Legal'
import { MatchCard } from './MatchResults'
import type { BlockedPlayer, MyCoach, PlayerMe, PlayerRecord, RecordGame, RecordTournament } from '../types'

interface Props {
  me: PlayerMe
  onChange: (me: PlayerMe) => void
  onOpenGame?: (gameId: number) => void
  /** Тренер создаёт групповую тренировку: экран создания с выбранным форматом */
  onCreateTraining?: () => void
  /** Заявки на корт (v0.35): мои брони и кабинет клуба */
  onMyBookings?: () => void
  onDesk?: (clubId: number) => void
}

const CALIBRATION_MATCHES = 10

/** «Пригласить друзей» (v0.34): личная ссылка — пришедший по ней отмечается
 *  как приглашённый вами, бот говорит спасибо. В Telegram — ссылка t.me,
 *  в WhatsApp и остальное — porcuatro.app/r/u<id> (там же App Store). */
function InviteFriends({ me }: { me: PlayerMe }) {
  const text = t().inviteFriendsText
  return (
    <div className="card">
      <div className="title">🎾 {t().inviteFriendsTitle}</div>
      <div className="muted small" style={{ marginTop: 4 }}>
        {t().inviteFriendsHint}
      </div>
      <div className="invite-friends">
        {me.invite_link_tg && (
          <button onClick={() => shareToChat(me.invite_link_tg, text)}>Telegram</button>
        )}
        <button
          className="ghost"
          onClick={() => openExternal(`https://wa.me/?text=${encodeURIComponent(`${text} ${me.invite_link_web}`)}`)}
        >
          WhatsApp
        </button>
        <button
          className="ghost"
          onClick={async () => notify((await copyText(`${text} ${me.invite_link_web}`)) ? t().linkCopied : me.invite_link_web)}
        >
          {t().copyLink}
        </button>
      </div>
      {me.referrals > 0 && (
        <div className="muted small" style={{ marginTop: 8 }}>
          {t().invitedCount(me.referrals)}
        </div>
      )}
    </div>
  )
}

function fmtDelta(d: number): string {
  const sign = d > 0 ? '+' : d < 0 ? '−' : '±'
  return `${sign}${Math.abs(d).toFixed(2)}`
}

/** Прошедшая игра в истории: где и с кем, счёт каждого матча и — прямо
 *  здесь — «Подтвердить» и «Оспорить». Раньше за этим нужно было искать
 *  экран игры, которой уже нет в ленте. */
function GameRow({
  item,
  meId,
  onOpenGame,
  onChange,
}: {
  item: RecordGame
  meId: number
  onOpenGame?: (id: number) => void
  onChange: () => void
}) {
  const date = new Date(item.played_at).toLocaleDateString(t().locale, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  })
  return (
    <div className="history-game">
      <div className="row between" style={{ gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700 }}>
            {date} · {item.club}
          </div>
          <div className="muted small">
            {t().playedWith}: {item.players.join(', ')}
            {!item.rated ? ` · ${t().friendlyBadge}` : ''}
          </div>
        </div>
        {onOpenGame && (
          <button className="ghost history-open" onClick={() => onOpenGame(item.game_id)}>
            {t().openGame}
          </button>
        )}
      </div>
      {item.matches.length === 0 ? (
        <div className="muted small" style={{ marginTop: 6 }}>
          {t().noScoreYet}
        </div>
      ) : (
        item.matches.map((match) => <MatchCard key={match.id} match={match} meId={meId} onChange={onChange} />)
      )}
    </div>
  )
}

const MEDAL: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' }

function TournamentRow({ item, onOpenGame }: { item: RecordTournament; onOpenGame?: (id: number) => void }) {
  const date = new Date(item.played_at).toLocaleDateString(t().locale, { day: '2-digit', month: '2-digit' })
  return (
    <div className="match-row">
      <button className="match-row-head" onClick={() => onOpenGame?.(item.game_id)}>
        <span className="result-mark gold" aria-hidden />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="row between" style={{ gap: 8 }}>
            <span>
              <b>
                {MEDAL[item.place] ? `${MEDAL[item.place]} ` : ''}
                {t().placeOf(item.place, item.of)}
              </b>
            </span>
            {item.delta != null && (
              <span className={item.delta > 0 ? 'badge' : item.delta < 0 ? 'badge warn' : 'badge neutral'}>
                {fmtDelta(item.delta)}
              </span>
            )}
          </span>
          <span className="muted small" style={{ display: 'block' }}>
            {date} · {item.club}
          </span>
          <span className="muted small" style={{ display: 'block' }}>
            {t().tournamentBadge(item.kind)} · {t().matchesWon(item.wins, item.matches)}
          </span>
        </span>
      </button>
    </div>
  )
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="stat">
      <div className="stat-value">{value}</div>
      <div className="muted small">{label}</div>
    </div>
  )
}

export default function Profile({ me, onChange, onOpenGame, onCreateTraining, onMyBookings, onDesk }: Props) {
  const [saving, setSaving] = useState(false)
  const [admin, setAdmin] = useState(false)
  const [adminDeals, setAdminDeals] = useState(false)
  const [adminCoaches, setAdminCoaches] = useState(false)
  const [coachEditor, setCoachEditor] = useState(false)
  const [coachInbox, setCoachInbox] = useState(false)
  const [myCoach, setMyCoach] = useState<MyCoach | null>(null)

  const loadCoach = useCallback(() => {
    if (!me.coach_status) return setMyCoach(null)
    api.myCoach().then(setMyCoach, () => undefined)
  }, [me.coach_status])

  useEffect(() => {
    loadCoach()
  }, [loadCoach])

  const refreshMe = useCallback(async () => {
    try {
      onChange(await api.me())
    } catch {
      /* профиль обновится при следующем входе */
    }
  }, [onChange])
  const [record, setRecord] = useState<PlayerRecord | null>(null)
  const [blocked, setBlocked] = useState<BlockedPlayer[]>([])
  useEffect(() => {
    api.blocks().then(setBlocked, () => undefined)
  }, [])

  const loadRecord = useCallback(() => {
    void api
      .record()
      .then(setRecord)
      .catch(() => setRecord({ summary: { matches: 0, wins: 0, losses: 0, draws: 0, games_won: 0, games_lost: 0, delta_30d: 0, best_level: null }, chart: [], matches: [], tournaments: [], games: [] }))
  }, [])

  useEffect(() => {
    loadRecord()
  }, [loadRecord, me.level])

  // Подтвердили или оспорили счёт прямо в истории: меняются и история, и уровень
  const afterMatchAction = useCallback(async () => {
    loadRecord()
    try {
      onChange(await api.me())
    } catch {
      /* профиль обновится при следующем входе */
    }
  }, [loadRecord, onChange])

  const sum = record?.summary
  // Игры и турниры — одной лентой, свежие сверху
  const history = [
    ...(record?.games ?? []).map((item) => ({ type: 'game' as const, at: item.played_at, item })),
    ...(record?.tournaments ?? []).map((item) => ({ type: 'tournament' as const, at: item.played_at, item })),
  ].sort((x, y) => y.at.localeCompare(x.at))

  const winRate = sum && sum.matches ? `${Math.round((100 * sum.wins) / sum.matches)}%` : '—'
  const games = sum ? `${sum.games_won}:${sum.games_lost}` : '—'

  async function patch(body: Partial<PlayerMe>) {
    setSaving(true)
    try {
      onChange(await api.updateMe(body))
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setSaving(false)
    }
  }

  async function linkTelegram() {
    setSaving(true)
    try {
      const { status } = await telegramLogin().result
      if (status === 'linked') {
        notify(t().linkDone)
        onChange(await api.me())
      } else if (status === 'taken') notify(t().linkTaken)
      else if (status === 'expired') notify(t().loginExpired)
    } catch (e) {
      notify(e instanceof Error ? e.message : t().loginFailed)
    } finally {
      setSaving(false)
    }
  }

  async function signOut() {
    if (!(await confirmAction(t().logoutConfirm))) return
    try {
      await api.logout()
    } catch {
      /* токен всё равно забываем */
    }
    setToken(null)
    window.location.reload()
  }

  async function removeAccount() {
    // Два подтверждения: действие необратимо
    if (!(await confirmAction(t().deleteAccountConfirm))) return
    if (!(await confirmAction(t().deleteAccountConfirm2))) return
    setSaving(true)
    try {
      await api.deleteAccount()
      setToken(null)
      if (isTelegramApp) tg?.close()
      else window.location.reload()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
      setSaving(false)
    }
  }

  const calibrating = me.rated_matches < CALIBRATION_MATCHES

  return (
    <div>
      <div className="big" style={{ margin: '4px 0 12px' }}>
        {me.first_name}
      </div>

      {me.is_admin && (
        <div className="row" style={{ gap: 8, marginBottom: 10 }}>
          <button className="ghost" style={{ flex: 1 }} onClick={() => setAdmin(true)}>
            ⚙️ {t().adminPlayers}
          </button>
          <button className="ghost" style={{ flex: 1 }} onClick={() => setAdminDeals(true)}>
            🏷 {t().adminDeals}
          </button>
          <button className="ghost" style={{ flex: 1 }} onClick={() => setAdminCoaches(true)}>
            🎓 {t().adminCoaches}
          </button>
        </div>
      )}
      {onDesk &&
        me.managed_clubs.map((c) => (
          <button key={c.id} className={c.pending ? '' : 'ghost'} style={{ width: '100%', marginBottom: 10 }} onClick={() => onDesk(c.id)}>
            {t().deskOpen(c.name, c.pending)}
          </button>
        ))}
      {onMyBookings && me.bookings_active > 0 && (
        <button className="ghost" style={{ width: '100%', marginBottom: 10 }} onClick={onMyBookings}>
          {t().profileBookings(me.bookings_active)}
        </button>
      )}
      {adminDeals && <AdminDeals onClose={() => setAdminDeals(false)} />}
      {adminCoaches && (
        <AdminCoaches
          onClose={() => {
            setAdminCoaches(false)
            void refreshMe()
          }}
        />
      )}
      {coachEditor && (
        <CoachEditor
          me={me}
          onClose={(changed) => {
            setCoachEditor(false)
            if (changed) void refreshMe().then(loadCoach)
          }}
        />
      )}
      {coachInbox && (
        <CoachInbox
          onClose={() => {
            setCoachInbox(false)
            loadCoach()
          }}
        />
      )}
      {admin && (
        <AdminPlayers
          onClose={async () => {
            setAdmin(false)
            // Админ мог поправить и свой уровень
            try {
              onChange(await api.me())
            } catch {
              /* профиль обновится при следующем входе */
            }
          }}
        />
      )}

      <div className="card">
        <div className="row between">
          <span className="muted">{t().yourLevel}</span>
          <span className="big">{me.level?.toFixed(2)}</span>
        </div>
        <div className="row between" style={{ marginTop: 8 }}>
          <span className="muted">
            {calibrating
              ? t().calibration(me.rated_matches, CALIBRATION_MATCHES)
              : t().calibrated}
          </span>
          <span>{me.level_confidence}%</span>
        </div>
        <div
          style={{
            height: 6,
            borderRadius: 999,
            background: 'var(--line)',
            marginTop: 8,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${me.level_confidence}%`,
              height: 6,
              background: 'var(--accent)',
            }}
          />
        </div>
        <div className="muted" style={{ marginTop: 10 }}>
          {t().ratingRule}
        </div>
      </div>

      <div className="card">
        <div className="row between">
          <span className="muted">{t().gamesPlayed}</span>
          <span className="title">{me.games_played}</span>
        </div>
        <div className="row between" style={{ marginTop: 8 }}>
          <span className="muted">{t().reliability}</span>
          <span className={me.reliability >= 70 ? 'badge' : 'badge warn'}>
            {me.reliability}%
          </span>
        </div>
      </div>

      <InviteFriends me={me} />

      {me.coach_status ? (
        <div className="card">
          <div className="row between" style={{ gap: 8 }}>
            <div className="title">🎓 {t().coachMyProfile}</div>
            <span className={`coach-status ${me.coach_status}`}>{t().coachStatus[me.coach_status].split(' — ')[0]}</span>
          </div>
          {me.coach_status === 'rejected' && (
            <div className="muted small" style={{ marginTop: 6 }}>
              {t().coachStatus.rejected}
            </div>
          )}
          <div className="coach-actions">
            <button className="ghost" onClick={() => setCoachEditor(true)}>
              ✏️ {t().coachEdit}
            </button>
            <button className={myCoach?.new_requests ? '' : 'ghost'} onClick={() => setCoachInbox(true)}>
              {t().coachInbox(myCoach?.new_requests ?? 0)}
            </button>
            {me.coach_status === 'approved' && onCreateTraining && (
              <button className="ghost" onClick={onCreateTraining}>
                {t().coachNewTraining}
              </button>
            )}
          </div>
        </div>
      ) : null}

      {sum && sum.matches > 0 && (
        <div className="stats">
          <Stat value={String(sum.matches)} label={t().statMatches} />
          <Stat value={winRate} label={t().statWins} />
          <Stat value={games} label={t().statGames} />
          <Stat value={fmtDelta(sum.delta_30d)} label={t().stat30d} />
        </div>
      )}

      <div className="card">
        <div className="row between">
          <div className="title">{t().levelChart}</div>
          {sum?.best_level != null && (
            <span className="muted small">{t().bestLevel(sum.best_level.toFixed(2))}</span>
          )}
        </div>
        {record === null && <div className="muted">{t().loading}</div>}
        {record && record.chart.length < 2 && (
          <div className="muted" style={{ marginTop: 6 }}>
            {t().chartEmpty}
          </div>
        )}
        {record && record.chart.length >= 2 && <LevelChart points={record.chart} />}
      </div>

      <div className="card">
        <div className="title">{t().gameHistory}</div>
        {record && history.length === 0 && (
          <div className="muted" style={{ marginTop: 6 }}>
            {t().noGameHistory}
          </div>
        )}
        {history.map((entry) =>
          entry.type === 'game' ? (
            <GameRow
              key={`g${entry.item.game_id}`}
              item={entry.item}
              meId={me.id}
              onOpenGame={onOpenGame}
              onChange={afterMatchAction}
            />
          ) : (
            <TournamentRow key={`t${entry.item.game_id}`} item={entry.item} onOpenGame={onOpenGame} />
          ),
        )}
      </div>

      {!me.coach_status && (
        <div className="card coach-cta">
          <div className="muted small">{t().coachCtaHint}</div>
          <button className="ghost" style={{ width: '100%', marginTop: 10 }} onClick={() => setCoachEditor(true)}>
            {t().coachCta}
          </button>
        </div>
      )}

      <label>{t().genderLabel}</label>
      <div className="chips">
        {(['m', 'f'] as const).map((value) => (
          <button
            key={value}
            className="chip"
            aria-pressed={me.gender === value}
            disabled={saving}
            onClick={() => patch({ gender: value })}
          >
            {value === 'm' ? t().genderMan : t().genderWoman}
          </button>
        ))}
      </div>

      <label>{t().onbSide}</label>
      <div className="chips">
        {(['left', 'right', 'both'] as const).map((value) => (
          <button
            key={value}
            className="chip"
            aria-pressed={me.side === value}
            disabled={saving}
            onClick={() => patch({ side: value })}
          >
            {t()[value]}
          </button>
        ))}
      </div>

      <label>Idioma / Language / Язык</label>
      <div className="chips">
        {(['es', 'en', 'ru'] as const).map((value) => (
          <button
            key={value}
            className="chip"
            aria-pressed={me.language === value}
            disabled={saving}
            onClick={async () => {
              setLang(value as Lang)
              await patch({ language: value })
            }}
          >
            {value.toUpperCase()}
          </button>
        ))}
      </div>
      {blocked.length > 0 && (
        <>
          <label>{t().blockedTitle}</label>
          <div className="card">
            {blocked.map((p) => (
              <div key={p.id} className="row between" style={{ padding: '4px 0' }}>
                <span>{p.first_name}</span>
                <button
                  className="ghost"
                  style={{ flex: '0 0 auto' }}
                  onClick={async () => {
                    await api.unblockPlayer(p.id).catch(() => undefined)
                    setBlocked((list) => list.filter((x) => x.id !== p.id))
                  }}
                >
                  {t().unblock}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
      <label>{t().accountTitle}</label>
      {!isTelegramApp && !me.telegram_id && (
        <div className="card">
          <div className="muted small">{t().linkTelegramHint}</div>
          <button style={{ width: '100%', marginTop: 10 }} disabled={saving} onClick={linkTelegram}>
            {t().linkTelegram}
          </button>
        </div>
      )}
      <div className="account-actions">
        {!isTelegramApp && (
          <button className="ghost" disabled={saving} onClick={signOut}>
            {t().logout}
          </button>
        )}
        <button className="danger" disabled={saving} onClick={removeAccount}>
          {t().deleteAccount}
        </button>
      </div>
      <Legal />
    </div>
  )
}
